/**
 * One flat group — the product-admin console's IA from `docs/ui-design/` (fleet
 * console): Dashboard, Tenants, Resellers, Rate cards (one item since TMMPDM-0089), Add-on catalog, Revenue,
 * Payouts, AI model prices (TMMPDM-0020), Parked payments (TMMPDM-0071, under Money), AI cost
 * reconciliation (TMMPDM-0088, under Money after Parked payments). Billing attention
 * (TMMPDM-0099) sits in Money between Parked payments and AI cost reconciliation.
 * Settings is not business navigation: it is pinned at the drawer's bottom, separated by a
 * divider (TMMPDM-0033, NAV-005). Support (TMMPDM-0109 "Tickets", renamed by TMMPDM-0110) sits with
 * Partners in the first group: both are the product's day-to-day operations over its tenants.
 * Merchant knowledge (TMMPDM-0112) follows Support there: another operational queue.
 */
import type { ReactNode } from 'react';
import DashboardIcon from '@mui/icons-material/Dashboard';
import HandshakeIcon from '@mui/icons-material/Handshake';
import ReceiptLongIcon from '@mui/icons-material/ReceiptLong';
import ExtensionIcon from '@mui/icons-material/Extension';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import PaymentsIcon from '@mui/icons-material/Payments';
import MemoryIcon from '@mui/icons-material/Memory';
import PauseCircleIcon from '@mui/icons-material/PauseCircle';
import TuneIcon from '@mui/icons-material/Tune';
import FactCheckIcon from '@mui/icons-material/FactCheck';
import NotificationImportantIcon from '@mui/icons-material/NotificationImportant';
import SupportAgentIcon from '@mui/icons-material/SupportAgent';
import StorefrontIcon from '@mui/icons-material/Storefront';

export type NavBadgeKey = never;

export interface NavItem {
  to: string;
  key: string;
  fallback: string;
  icon: ReactNode;
  badge?: NavBadgeKey;
}

export interface NavGroup {
  groupKey: string;
  groupFallback: string;
  items: NavItem[];
}

export const NAV_GROUPS: NavGroup[] = [
  {
    groupKey: 'nav.group.platform',
    groupFallback: '',
    items: [
      { to: '/dashboard', key: 'nav.dashboard', fallback: 'Dashboard', icon: <DashboardIcon /> },
      {
        to: '/partners',
        key: 'nav.partners',
        fallback: 'Partners',
        icon: <HandshakeIcon />,
      },
      // TMMPDM-0109: members' support cases and feature requests, triaged here (an operational screen).
      // TMMPDM-0110: named "Support" now that it holds announcements too; the path stays `/tickets`.
      { to: '/tickets', key: 'nav.tickets', fallback: 'Support', icon: <SupportAgentIcon /> },
      // TMMPDM-0112: the merchant-pattern approval queue and the unlisted banks (tmm-api 0194, 0195).
      {
        to: '/merchant-knowledge',
        key: 'nav.merchantKnowledge',
        fallback: 'Merchant knowledge',
        icon: <StorefrontIcon />,
      },
    ],
  },
  {
    groupKey: 'nav.group.pricing',
    groupFallback: 'Pricing',
    items: [
      {
        to: '/rate-cards',
        key: 'nav.rateCards',
        fallback: 'Rate cards',
        icon: <ReceiptLongIcon />,
      },
      { to: '/add-ons', key: 'nav.addOns', fallback: 'Add-on catalog', icon: <ExtensionIcon /> },
      {
        to: '/ai-model-prices',
        key: 'nav.aiModelPrices',
        fallback: 'AI model prices',
        icon: <MemoryIcon />,
      },
    ],
  },
  {
    groupKey: 'nav.group.money',
    groupFallback: 'Money',
    items: [
      { to: '/revenue', key: 'nav.revenue', fallback: 'Revenue', icon: <TrendingUpIcon /> },
      { to: '/payouts', key: 'nav.payouts', fallback: 'Payouts', icon: <PaymentsIcon /> },
      {
        to: '/billing/parked-events',
        key: 'nav.parkedPayments',
        fallback: 'Parked payments',
        icon: <PauseCircleIcon />,
      },
      {
        to: '/billing-attention',
        key: 'nav.billingAttention',
        fallback: 'Billing attention',
        icon: <NotificationImportantIcon />,
      },
      {
        to: '/ai-cost-reconciliation',
        key: 'nav.aiCostReconciliation',
        fallback: 'AI cost reconciliation',
        icon: <FactCheckIcon />,
      },
    ],
  },
];

export const SETTINGS_NAV_ITEM: NavItem = {
  to: '/product-settings',
  key: 'nav.settings',
  fallback: 'Settings',
  icon: <TuneIcon />,
};
